/**
 * ★ Fase 68 — Recupero lead persi (Funnel IT) · solo admin.
 * Import lista CSV/XLSX → stage Perso + tag «recupero persi» (Claudia),
 * anteprima/prova delle 3 email e invio graduale con tetto giornaliero.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Upload, Send, Mail, PlayCircle, RotateCcw } from 'lucide-react';

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border p-3" data-testid={testId}>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-xl font-semibold tabular-nums">{value ?? '—'}</div>
  </div>
);

function ImportCard({ onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/lost-recovery/import', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      setRes(r.data);
      toast.success(`Import completato: ${r.data.created} nuovi lead`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import non riuscito.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card data-testid="lost-import-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Upload className="h-5 w-5" /> Importa lead persi</CardTitle>
        <CardDescription>File CSV o Excel (anche senza intestazione): piattaforma, nome, cognome, email, telefono, città. I lead entrano in «Perso» con il tag «recupero persi», assegnati a Claudia. Le email già presenti ricevono solo il tag.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Input type="file" accept=".csv,.xlsx,.xlsm" className="max-w-sm" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="lost-import-file" />
          <Button onClick={run} disabled={!file || busy} data-testid="lost-import-submit">{busy ? 'Import in corso…' : 'Importa lead persi'}</Button>
        </div>
        {res && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="lost-import-result">
            <Stat label="Righe lette" value={res.rows} testId="lost-import-rows" />
            <Stat label="Nuovi lead creati" value={res.created} testId="lost-import-created" />
            <Stat label="Già presenti (tag aggiunto)" value={res.updated_existing} testId="lost-import-updated" />
            <Stat label="Doppioni nel file" value={res.dup_in_file} testId="lost-import-dups" />
            <Stat label="Email non valide scartate" value={res.invalid_email} testId="lost-import-invalid" />
            <Stat label="Email corrette (es. .con → .com)" value={res.fixed_email} testId="lost-import-fixed" />
            <Stat label="Dati di test scartati" value={res.test_skipped} testId="lost-import-test" />
            <Stat label="Iscritti alla campagna" value={res.enrolled} testId="lost-import-enrolled" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EmailsCard() {
  const [step, setStep] = useState(1);
  const [prev, setPrev] = useState(null);
  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  useEffect(() => {
    setPrev(null);
    api.get(`/lost-recovery/preview/${step}`).then((r) => setPrev(r.data)).catch(() => toast.error('Anteprima non disponibile.'));
  }, [step]);
  const sendTest = async () => {
    setSending(true);
    try {
      const r = await api.post('/lost-recovery/test-email', { email: to });
      if (r.data.ok) toast.success(`3 email di prova inviate a ${to}`);
      else toast.error('Invio di prova non riuscito', { description: r.data.results.map((x) => x.error).filter(Boolean)[0] });
    } catch (e) {
      toast.error(e?.response?.status === 422 ? 'Indirizzo email non valido.' : (e?.response?.data?.detail || 'Invio di prova non riuscito.'));
    } finally {
      setSending(false);
    }
  };
  return (
    <Card data-testid="lost-emails-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Mail className="h-5 w-5" /> Le 3 email di recupero</CardTitle>
        <CardDescription>Giorno 0 · giorno 3 · giorno 7 — firmate «Team HCF Property», con il video del Verification Pass. «Sì» sposta il lead in Risposta e avvisa Claudia su Telegram; «No» lo disiscrive.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3].map((s) => (
            <Button key={s} size="sm" variant={step === s ? 'default' : 'outline'} onClick={() => setStep(s)} data-testid={`lost-preview-tab-${s}`}>Email {s} · giorno {[0, 3, 7][s - 1]}</Button>
          ))}
        </div>
        <div className="text-sm"><span className="text-muted-foreground">Oggetto:</span> <b data-testid="lost-preview-subject">{prev?.subject || '…'}</b></div>
        <iframe title="Anteprima email" srcDoc={prev?.html || ''} className="w-full h-[640px] rounded-lg border bg-white" data-testid="lost-preview-frame" />
        <div className="flex flex-wrap items-center gap-3">
          <Input type="email" placeholder="la-tua@email.com" value={to} onChange={(e) => setTo(e.target.value)} className="max-w-xs" data-testid="lost-test-email-input" />
          <Button variant="outline" className="gap-2" disabled={!to || sending} onClick={sendTest} data-testid="lost-test-email-send"><Send className="h-4 w-4" /> {sending ? 'Invio…' : 'Inviami le 3 email di prova'}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CampaignCard({ st, reload }) {
  const [active, setActive] = useState(false);
  const [cap, setCap] = useState(250);
  useEffect(() => { if (st) { setActive(st.active); setCap(st.daily_cap); } }, [st]);
  const save = async (nextActive = active) => {
    try {
      await api.post('/lost-recovery/settings', { active: nextActive, daily_cap: Number(cap) || 250 });
      toast.success(nextActive ? 'Invio automatico ATTIVO' : 'Impostazioni salvate');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Salvataggio non riuscito.');
    }
  };
  const runNow = async () => {
    try {
      await api.post('/lost-recovery/run-now');
      toast.success('Lotto di oggi in invio', { description: 'Aggiorna lo stato tra qualche minuto.' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invio non avviato.');
    }
  };
  return (
    <Card data-testid="lost-campaign-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><PlayCircle className="h-5 w-5" /> Invio automatico
          <Badge variant={st?.active ? 'default' : 'secondary'} data-testid="lost-campaign-badge">{st?.active ? 'Attivo' : 'Spento'}</Badge>
        </CardTitle>
        <CardDescription>Ogni mattina alle 9:30 parte il lotto del giorno, fino al tetto impostato: prima i richiami (email 2 e 3), poi i nuovi contatti.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-3 text-sm font-medium">
            <Switch checked={active} onCheckedChange={(v) => { setActive(v); save(v); }} data-testid="lost-campaign-switch" /> Invio attivo
          </label>
          <label className="flex items-center gap-2 text-sm">Email al giorno
            <Input type="number" min={1} max={2000} value={cap} onChange={(e) => setCap(e.target.value)} className="w-24 h-9" data-testid="lost-campaign-cap" />
          </label>
          <Button size="sm" variant="outline" onClick={() => save()} data-testid="lost-campaign-save">Salva</Button>
          <Button size="sm" variant="outline" className="gap-2" disabled={!st?.active} onClick={runNow} data-testid="lost-campaign-run-now"><Send className="h-4 w-4" /> Invia ora il lotto di oggi</Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="lost-campaign-stats">
          <Stat label="Lead «recupero persi»" value={st?.total} testId="lost-stat-total" />
          <Stat label="In attesa dell'email 1" value={st?.waiting_first} testId="lost-stat-waiting" />
          <Stat label="Ricevuta email 1" value={st?.after_email1} testId="lost-stat-e1" />
          <Stat label="Ricevuta email 2" value={st?.after_email2} testId="lost-stat-e2" />
          <Stat label="Sequenza completata" value={st?.completed} testId="lost-stat-done" />
          <Stat label="Hanno cliccato «Sì»" value={st?.replied_yes} testId="lost-stat-yes" />
          <Stat label="Disiscritti" value={st?.unsubscribed} testId="lost-stat-unsub" />
          <Stat label="Inviate oggi" value={st ? `${st.sent_today} / ${st.daily_cap}` : null} testId="lost-stat-today" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function LostRecoveryPage() {
  const [st, setSt] = useState(null);
  const load = useCallback(() => {
    api.get('/lost-recovery/status').then((r) => setSt(r.data)).catch(() => toast.error('Stato non disponibile.'));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="p-4 md:p-8 max-w-6xl space-y-6" data-testid="lost-recovery-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Recupero lead persi · Funnel IT</h1>
          <p className="text-sm text-muted-foreground">Importa la lista, approva le email con una prova e poi attiva l'invio graduale.</p>
        </div>
        <Button variant="outline" size="sm" className="gap-2" onClick={load} data-testid="lost-refresh"><RotateCcw className="h-4 w-4" /> Aggiorna</Button>
      </div>
      <ImportCard onDone={load} />
      <EmailsCard />
      <CampaignCard st={st} reload={load} />
    </div>
  );
}
